<template>
  <div :class="$style.modalContainer">
    <div :class="$style.header">
      <h3 :class="$style.title">
        {{ weekdayNames.get(selectedDayOfWeek) }}のスケジュール
      </h3>
      <IconClose :class="$style.closeIcon" @click="cancel" />
    </div>

    <div :class="$style.timeSlotList">
      <div
        v-for="(slot, index) in timeSlots"
        :key="slot.id"
        :class="$style.timeSlotRow"
      >
        <div :class="$style.timeInputContainer">
          <div :class="$style.timeInput">
            <DolphinTextInput
              v-model="timeSlots[index].fromText"
              :class="$style.timeInputField"
              :invalid="!!timeSlotErrors[index]?.from"
              placeholder="--:--"
            />
            <IconTimer :class="$style.timeInputIcon" />
          </div>
          <ErrorMessageHandler :error="timeSlotErrors[index]?.from" />
        </div>
        <span :class="$style.timeRangeDash">−</span>
        <div :class="$style.timeInputContainer">
          <div :class="$style.timeInput">
            <DolphinTextInput
              v-model="timeSlots[index].untilText"
              :class="$style.timeInputField"
              :invalid="!!timeSlotErrors[index]?.until"
              placeholder="--:--"
            />
            <IconTimer :class="$style.timeInputIcon" />
          </div>
          <ErrorMessageHandler :error="timeSlotErrors[index]?.until" />
        </div>
        <ButtonIconOnly
          :class="[$style.iconButton, $style.iconButton__delete]"
          @click="removeTimeSlot(index)"
        >
          <template #icon><IconTrash /></template>
        </ButtonIconOnly>
        <ButtonIconOnly
          :class="[$style.iconButton, $style.iconButton__add]"
          @click="addTimeSlot(index + 1)"
        >
          <template #icon><IconPlus /></template>
        </ButtonIconOnly>
      </div>

      <div v-if="timeSlots.length === 0" :class="$style.emptyState">
        <FlatButton
          :class="$style.addFirstSlotButton"
          @click="addTimeSlot(0)"
          variant="primary"
        >
          <IconPlus :class="$style.addFirstSlotIcon" />
          <span>時間枠を追加</span>
        </FlatButton>
      </div>
    </div>

    <div
      v-if="timeSlotErrors.some(err => err.general)"
      :class="$style.generalError"
    >
      {{ timeSlotErrors.find(err => err.general)?.general }}
    </div>

    <div :class="$style.otherDaysSection">
      <p :class="$style.otherDaysLabel">
        他の曜日にも適用する（上書きされます）
      </p>
      <div :class="$style.weekdayButtons">
        <div
          v-for="[dayOfWeek, dayName] in weekdayNames"
          :key="dayOfWeek"
          :class="[
            $style.weekdayButton,
            {
              [$style.selected]: selectedDays[dayOfWeek],
              [$style.disabled]: dayOfWeek === selectedDayOfWeek,
            },
          ]"
          @click="dayOfWeek !== selectedDayOfWeek && toggleDay(dayOfWeek)"
        >
          {{ dayName.charAt(0) }}
        </div>
      </div>
    </div>

    <div :class="$style.footer">
      <FlatButton :class="$style.cancelButton" variant="base" @click="cancel">
        キャンセル
      </FlatButton>
      <FlatButton :class="$style.saveButton" variant="primary" @click="save">
        保存する
      </FlatButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import DolphinTextInput from '@/components/atoms/DolphinTextInput.vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import ButtonIconOnly from '@/components/atoms/ButtonIconOnly.vue'
import IconClose from '@/components/icons/IconClose.vue'
import IconTimer from '@/components/icons/IconTimer.vue'
import IconTrash from '@/components/icons/IconTrash.vue'
import IconPlus from '@/components/icons/IconPlus.vue'
import ErrorMessageHandler from '@/components/atoms/ErrorMessageHandler.vue'

import {
  DayOfWeek,
  type CalendarDailyAvailabilityInput,
  type CalendarTimeOfDayInput,
  type DayOfWeek as DayOfWeekType,
} from '@/types/schema.generated.ts'

import { weekdayNames } from './calendar.ts'

const props = defineProps<{
  selectedDayOfWeek: DayOfWeekType
  availabilities: CalendarDailyAvailabilityInput[]
}>()

const emit = defineEmits<{
  (event: 'cancel'): void
  (
    event: 'save',
    data: {
      timeSlots: CalendarDailyAvailabilityInput[]
      selectedDayOfWeeks: DayOfWeek[]
    }
  ): void
}>()

const formatTimeOfDay = (time: CalendarTimeOfDayInput): string => {
  const hours = time.hours.toString().padStart(2, '0')
  const minutes = time.minutes.toString().padStart(2, '0')
  return `${hours}:${minutes}`
}

const parseTimeString = (timeStr: string): CalendarTimeOfDayInput | null => {
  if (!timeStr || timeStr === '') return null

  const match = timeStr.match(/^(\d{1,2}):(\d{2})$/)
  if (!match) return null

  const hours = parseInt(match[1], 10)
  const minutes = parseInt(match[2], 10)

  if (
    isNaN(hours) ||
    isNaN(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null
  }

  return { hours, minutes }
}

type TimeSlotWithTextType = {
  id: number
  fromText: string
  untilText: string
  from: CalendarTimeOfDayInput | null
  until: CalendarTimeOfDayInput | null
}

type TimeSlotErrorType = {
  from?: string
  until?: string
  general?: string
}

const initialTimeSlots = computed(() => {
  // Convert frontend day index (0-6) to backend day index (1-7) for comparison
  const daySlots = props.availabilities.filter(
    slot => slot.dayOfWeek === props.selectedDayOfWeek
  )

  if (daySlots.length === 0) {
    return []
  }

  return daySlots.map(
    (slot: CalendarDailyAvailabilityInput, index: number) => ({
      id: index,
      fromText: formatTimeOfDay(slot.from),
      untilText: formatTimeOfDay(slot.until),
      from: slot.from,
      until: slot.until,
    })
  )
})

const timeSlots = ref<TimeSlotWithTextType[]>([])
const timeSlotErrors = ref<TimeSlotErrorType[]>([])
const selectedDays = ref<Record<DayOfWeekType, boolean>>(
  (Object.values(DayOfWeek) as DayOfWeekType[]).reduce(
    (acc, dow) => {
      acc[dow] = false
      return acc
    },
    {} as Record<DayOfWeekType, boolean>
  )
)

// Initialize data
onMounted(() => {
  timeSlots.value =
    initialTimeSlots.value.length > 0 ? initialTimeSlots.value : [] // Initialize with empty array to show empty state
})

// Methods
const toggleDay = (dow: DayOfWeekType) => {
  selectedDays.value = {
    ...selectedDays.value,
    [dow]: !selectedDays.value[dow],
  }
}

const addTimeSlot = (index: number) => {
  const lastSlot = timeSlots.value.at(-1)
  if (!lastSlot) {
    timeSlots.value = [
      {
        id: 0,
        fromText: '',
        untilText: '',
        from: null,
        until: null,
      },
    ]
    timeSlotErrors.value = [{}]
    return
  }

  const newID = lastSlot.id + 1

  const newSlot = {
    id: newID,
    fromText: '',
    untilText: '',
    from: null,
    until: null,
  }

  timeSlots.value = [
    ...timeSlots.value.slice(0, index),
    newSlot,
    ...timeSlots.value.slice(index),
  ]
  timeSlotErrors.value = [
    ...timeSlotErrors.value.slice(0, index),
    {},
    ...timeSlotErrors.value.slice(index),
  ]
}

const removeTimeSlot = (index: number) => {
  timeSlots.value = [
    ...timeSlots.value.slice(0, index),
    ...timeSlots.value.slice(index + 1),
  ]

  timeSlotErrors.value = [
    ...timeSlotErrors.value.slice(0, index),
    ...timeSlotErrors.value.slice(index + 1),
  ]
}

const validateTimeSlots = (): boolean => {
  // Initialize with new empty error objects
  timeSlotErrors.value = timeSlots.value.map(() => ({}))

  const validationResult = timeSlots.value.reduce((acc, slot, index) => {
    const from = parseTimeString(slot.fromText)
    const until = parseTimeString(slot.untilText)
    const newErrors = { ...timeSlotErrors.value[index] }

    const hasFromError = !from
    const hasUntilError = !until
    const hasRangeError =
      from &&
      until &&
      from.hours * 60 + from.minutes >= until.hours * 60 + until.minutes

    if (!from) {
      newErrors.from = 'HH:MM 形式で入力してください'
    }

    if (!until) {
      newErrors.until = 'HH:MM 形式で入力してください'
    }

    if (hasRangeError) {
      newErrors.general = '終了時間は開始時間より後である必要があります'
    }

    // Update the timeSlots
    if (from && until) {
      timeSlots.value = timeSlots.value.map(
        (slot: TimeSlotWithTextType, i: number): TimeSlotWithTextType => {
          return i === index ? { ...slot, from, until } : slot
        }
      )
    }

    // Update the error in timeSlotErrors array
    timeSlotErrors.value = timeSlotErrors.value.map(
      (v: TimeSlotErrorType, i: number): TimeSlotErrorType => {
        return i === index ? newErrors : v
      }
    )

    // Return true only if no errors were detected
    const isCurrentValid = !hasFromError && !hasUntilError && !hasRangeError
    return acc && isCurrentValid
  }, true)

  return validationResult
}

const save = () => {
  if (!validateTimeSlots()) return

  // Convert to CalendarDailyAvailabilityInput[]
  const validTimeSlots = timeSlots.value
    .filter(slot => slot.from && slot.until)
    .map(slot => ({
      dayOfWeek: props.selectedDayOfWeek,
      from: slot.from!,
      until: slot.until!,
    }))

  const selectedDOWs = Object.entries(selectedDays.value)
    .filter(([_, selected]) => selected)
    .map(([dow]) => dow as DayOfWeekType)

  emit('save', {
    timeSlots: validTimeSlots,
    selectedDayOfWeeks: selectedDOWs,
  })
}

const cancel = () => {
  emit('cancel')
}
</script>

<style lang="scss" module>
.modalContainer {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 540px;
  padding: 24px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.title {
  font-size: 18px;
  font-weight: bold;
  color: $color-text-and-object-high-emphasis;
  margin: 0;
}

.closeIcon {
  cursor: pointer;
  font-size: 20px;
  color: $color-text-and-object-medium-emphasis;
}

.timeSlotList {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
}

.timeSlotRow {
  display: flex;
  align-items: center;
  gap: 8px;
}

.timeInputContainer {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.timeInput {
  position: relative;
  width: 100%;
}

.timeInputField {
  width: 100%;
  text-align: center;
}

.timeInputIcon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: $color-text-and-object-medium-emphasis;
  font-size: 18px;
}

.timeRangeDash {
  font-size: 18px;
  color: $color-text-and-object-medium-emphasis;
}

.iconButton {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 36px;
  height: 36px;
  border-radius: 4px;
  border: 1px solid $color-border-low-emphasis;
  background-color: $color-surface-primary;
  cursor: pointer;

  &__delete {
    color: $color-caution-accent;
    border-color: $color-caution-accent;
    font-size: 16px;
  }

  &__add {
    color: $color-blue-700;
    border-color: $color-blue-700;
    font-size: 16px;
  }

  &:hover {
    background-color: $color-surface-secondary;
  }
}

.emptyState {
  display: flex;
  justify-content: center;
  padding: 16px 0;
}

.addFirstSlotButton {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 16px 0;
  cursor: pointer;
}

.addFirstSlotIcon {
  margin-right: 8px;
}

.generalError {
  color: $color-caution-accent;
  font-size: 13px;
  margin-bottom: 16px;
}

.otherDaysSection {
  margin-bottom: 24px;
}

.otherDaysLabel {
  font-size: 14px;
  color: $color-text-and-object-medium-emphasis;
  margin-bottom: 12px;
}

.weekdayButtons {
  display: flex;
  width: 100%;
  border: 1px solid $color-border-low-emphasis;
  border-radius: 4px;
  overflow: hidden;
}

.weekdayButton {
  flex: 1;
  text-align: center;
  padding: 8px 0;
  font-size: 14px;
  color: $color-text-and-object-high-emphasis;
  cursor: pointer;
  user-select: none;
  border-right: 1px solid $color-border-low-emphasis;

  &:last-child {
    border-right: none;
  }

  &:hover:not(.disabled) {
    background-color: $color-surface-tertiary;
  }

  &.selected {
    background-color: $color-surface-accent-primary;
    color: $color-text-and-object-high-emphasis-inverse;
  }

  &.disabled {
    opacity: 0.5;
    pointer-events: none;
    background-color: $color-surface-secondary;
  }
}

.footer {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
}

.cancelButton {
  min-width: 100px;
}

.saveButton {
  min-width: 100px;
}
</style>
